import React, { useState } from 'react';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { RowsSkeleton } from './DashboardSkeleton';
import { Avatar, Card, EmptyState, InlineError, ListRow, SectionHeader, SegmentedControl, TextButton } from './ui';
import { valueTone } from './theme';
import { formatInteger, formatMoney } from './format';

const COLLAPSED_ROWS = 5;

/** Players who brought in the most GGR, or who won the most (most negative GGR), in the period. */
export default function TopPlayersCard({ t, currency, players, playerCount, periodName, onLoad, onSelect, className = '' }) {
  const [view, setView] = useState('top');
  const [expanded, setExpanded] = useState(false);
  const { status, ranking } = players;

  const header = (
    <SectionHeader
      icon={TrophyIcon}
      title={t('playersCard', 'Players')}
      subtitle={periodName}
      action={(
        <SegmentedControl
          label={t('topPlayersView', 'Player ranking')}
          value={view}
          onChange={(next) => {
            setView(next);
            setExpanded(false);
          }}
          options={[
            { value: 'top', label: t('topGgr', 'Top GGR') },
            { value: 'winners', label: t('bigWinners', 'Big winners') }
          ]}
          buttonClassName="px-2.5 py-1.5 text-[11px]"
        />
      )}
    />
  );

  let body;
  if (status === 'manual') {
    body = (
      <div className="py-5 text-center">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {t('playersOnRequest', 'Your network has {{count}} players, so their totals load on request.', { count: formatInteger(playerCount) })}
        </p>
        <TextButton onClick={onLoad} className="mt-2 text-sm">{t('loadPlayerTotals', 'Load player totals')}</TextButton>
      </div>
    );
  } else if (!ranking && status === 'error') {
    body = <InlineError>{t('playersFailed', 'Could not load player totals.')}</InlineError>;
  } else if (!ranking) {
    body = <RowsSkeleton />;
  } else {
    const list = view === 'top' ? ranking.top : ranking.winners;
    const visible = expanded ? list : list.slice(0, COLLAPSED_ROWS);
    body = list.length === 0 ? (
      <EmptyState title={view === 'top' ? t('noTopPlayers', 'No player has brought in GGR yet') : t('noWinners', 'No player is ahead in this period')} />
    ) : (
      <>
        <div className="-mx-2">
          {visible.map((player) => (
            <ListRow
              key={player.username}
              onClick={() => onSelect(player)}
              leading={<Avatar name={player.name} seed={player.username} />}
              label={player.name}
              value={formatMoney(player.ggr, currency)}
              valueClassName={valueTone(player.ggr)}
            />
          ))}
        </div>
        {list.length > COLLAPSED_ROWS && (
          <TextButton onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} className="mt-1">
            {expanded ? t('showLess', 'Show less') : t('showAll', 'Show all ({{count}})', { count: list.length })}
          </TextButton>
        )}
      </>
    );
  }

  return (
    <Card className={className}>
      {header}
      {body}
    </Card>
  );
}
